<template>
  <div
    v-if="!selected_meeting"
    class="cursor-default flex h-full min-w-[650px] relative bg-gradient-to-br from-gray-50 to-white dark:from-dark-box-background dark:to-dark-screen-background rounded-lg">
    <!-- Modern Header -->
    <div class="absolute left-6 top-6 z-20">
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
        {{ label }}
      </h2>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ $moment(selected_date).format("MMMM YYYY") }}
      </p>
    </div>

    <!-- Calendar Container -->
    <div :class="[columns == 1 ? 'w-[580px]' : 'w-[900px]']" class="relative">
      <Calendar
        ref="calendar"
        :locale="$store.state.portfolio.ui_options?.language?.replace(' ', '') || 'en'"
        :view="viewOption == 'week_view' ? 'weekly' : 'monthly'"
        expanded
        :first-day-of-week="$store.state.portfolio.ui_options?.language?.replace(' ', '') == 'ar' ? 1 : 2"
        :columns="columns"
        title-position="center"
        class="z-10 pt-16 !border-none w-full"
        :masks="masks"
        :class="[`${$store.state.ui.darkMode ? 'custom-calendar-dark !bg-transparent' : 'custom-calendar !bg-transparent'}`]"
        :attributes="attributes.concat(date_selected_attribute)"
        :is-dark="$store.state.ui.darkMode"
        @did-move="handleUpdateCalendar"
        @dayclick="handleDayClick" />
    </div>
    <!-- Modern Search and Filters -->
    <div v-if="!create_new_event && event_items.length" class="absolute right-6 flex items-center gap-3 top-6 z-20">
      <div class="relative">
        <TextInput
          :validate-on-input="true"
          label=""
          :classes="'w-[240px]'"
          input-classes="appearance-none h-10 block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm bg-white dark:bg-dark-box-background text-gray-900 dark:text-white transition-all"
          name="search_term"
          type="text"
          :placeholder="$t('Search events...')"
          :debounce="500"
          @update:modelValue="handleSearch" />
        <svg class="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      <MultiSelectInput
        v-if="$utils.isEmpty(initialFilter)"
        v-model:current-items="team_members_selected"
        name="team_members_selected"
        :items="$store.state.data?.users"
        label-attr="full_name"
        :allow-null="true"
        value-attr="id"
        :label="$t('Users')"
        classes="w-[200px] dark:text-dark-body-text"
        input-classes="sm:text-sm appearance-none font-medium text-gray-700 dark:text-gray-300 text-left block w-full px-3 py-2 h-10 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-dark-box-background focus:ring-2 focus:outline-none focus:ring-blue-500 focus:border-transparent transition-all"
        selection-text="Members (%COUNT)"
        :placeholder="$t('Team Members')"
        :show-label="false" />
    </div>
    <!-- Modern Filter Controls -->
    <div v-if="view_by != 1" class="flex absolute z-10 bottom-6 left-6 gap-2 flex-wrap">
      <!-- View Toggle Button -->
      <button
        class="flex items-center px-4 py-2 rounded-lg bg-white dark:bg-dark-box-background border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all shadow-sm hover:shadow-md group"
        @click="columns = columns == 1 ? 2 : 1">
        <Calendar1 v-if="columns == 2" class="w-4 h-4 mr-2 text-blue-500" aria-hidden="true" />
        <CalendarDays v-else class="w-4 h-4 mr-2 text-blue-500" aria-hidden="true" />
        <span class="text-sm font-medium">
          {{
            columns == 1
              ? `${$t("Show")} 2 ${viewOption == "week_view" ? $t("Weeks") : $t("Months")}`
              : `${$t("Show")} 1 ${viewOption == "week_view" ? $t("Week") : $t("Month")}`
          }}
        </span>
      </button>

      <!-- Category Filter Badges -->
      <button
        v-if="view_by?.includes('plans')"
        :class="[
          'flex items-center px-4 py-2 rounded-lg border-2 transition-all shadow-sm hover:shadow-md text-sm font-medium',
          show_plans
            ? 'bg-[#FFBE15]/10 border-[#FFBE15] text-[#FFBE15] dark:bg-[#FFBE15]/20'
            : 'bg-white dark:bg-dark-box-background border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
        ]"
        @click="show_plans = !show_plans">
        <span :class="['rounded-full w-3 h-3 mr-2', show_plans ? 'bg-[#FFBE15]' : 'border-2 border-[#FFBE15]']" />
        {{ $t("Plans") }}
      </button>

      <button
        v-if="view_by?.includes('goals')"
        :class="[
          'flex items-center px-4 py-2 rounded-lg border-2 transition-all shadow-sm hover:shadow-md text-sm font-medium',
          show_goals
            ? 'bg-[#59BEE5]/10 border-[#59BEE5] text-[#59BEE5] dark:bg-[#59BEE5]/20'
            : 'bg-white dark:bg-dark-box-background border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
        ]"
        @click="show_goals = !show_goals">
        <span :class="['rounded-full w-3 h-3 mr-2', show_goals ? 'bg-[#59BEE5]' : 'border-2 border-[#59BEE5]']" />
        {{ $t("Goals") }}
      </button>

      <button
        v-if="view_by?.includes('tasks')"
        :class="[
          'flex items-center px-4 py-2 rounded-lg border-2 transition-all shadow-sm hover:shadow-md text-sm font-medium',
          show_tasks
            ? 'bg-[#B171F9]/10 border-[#B171F9] text-[#B171F9] dark:bg-[#B171F9]/20'
            : 'bg-white dark:bg-dark-box-background border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
        ]"
        @click="show_tasks = !show_tasks">
        <span :class="['rounded-full w-3 h-3 mr-2', show_tasks ? 'bg-[#B171F9]' : 'border-2 border-[#B171F9]']" />
        {{ $t("Tasks") }}
      </button>

      <button
        v-if="view_by?.includes('interactions')"
        :class="[
          'flex items-center px-4 py-2 rounded-lg border-2 transition-all shadow-sm hover:shadow-md text-sm font-medium',
          show_interactions
            ? 'bg-[#5CE0A9]/10 border-[#5CE0A9] text-[#5CE0A9] dark:bg-[#5CE0A9]/20'
            : 'bg-white dark:bg-dark-box-background border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
        ]"
        @click="show_interactions = !show_interactions">
        <span :class="['rounded-full w-3 h-3 mr-2', show_interactions ? 'bg-[#5CE0A9]' : 'border-2 border-[#5CE0A9]']" />
        {{ $t("Interactions") }}
      </button>
    </div>

    <div
      v-if="!create_new_event && !event_items.length && !is_calendar_connected"
      class="relative w-full h-full z-20 bg-white rounded-r-lg dark:bg-dark-box-background">
      <div class="relative h-full mx-5">
        <span class="absolute inset-0 top-10 flex justify-center z-40 font-bold text-black">{{ $t("Integrate Your Favorite Collaborations Tool") }}
        </span>
        <div class="absolute inset-0 flex items-center justify-center top-28 z-40">
          <div class="flex flex-col items-center">
            <span class="text-black text-center">{{ $t("Create and view your emails, meetings, chats in RevSetter") }}</span>
            <div
              class="flex items-center mt-2 cursor-pointer border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] px-6 font-bold text-white rounded-full w-fit-content h-[50px] content-center text-[14px]"
              @click="$router.push('/admin/integrations')">
              <ArrowRight class="text-white w-7 h-7 ml-1 mr-3" />
              <span>{{ $t("Go To Integration") }}</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center justify-center w-full h-full">
          <img class="relative h-full" src="@/assets/calendar-not-connected.png">
        </div>
      </div>
    </div>
    <div
      v-else-if="!create_new_event && !event_items.length && is_calendar_connected && !loading"
      class="relative w-[70%] ml-[10%] h-full z-0 bg-white rounded-r-lg dark:bg-dark-box-background">
      <div class="relative h-full mx-5">
        <span class="absolute inset-0 top-10 flex justify-center z-40 font-bold text-black">{{ $t("No Events or Activities on this Date") }}
        </span>
        <div class="absolute inset-0 flex items-center justify-center top-28 z-40">
          <div class="flex flex-col items-center">
            <span class="text-black text-center">{{
              $t("Add an Interaction or Go to your Activities to add Plans, Goals or Tasks.")
            }}</span>
            <div
              class="flex items-center mt-2 cursor-pointer border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] px-6 font-bold text-white rounded-full w-fit-content h-[50px] content-center text-[14px]"
              @click="editing_disabled ? null : handleCreateMeetingClick()">
              <Plus class="text-white w-7 h-7 ml-1 mr-3" />
              <span>{{ $t("Add Meeting") }}</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 flex items-center justify-center w-full h-full">
          <img class="relative h-full" src="@/assets/calendar-not-connected.png">
        </div>
      </div>
    </div>

    <!-- Modern Events List -->
    <div
      v-else-if="!create_new_event"
      :class="[viewOption == 'week_view' ? 'h-[calc(100vh-200px)]' : 'h-[calc(100vh-250px)]']"
      class="w-full mt-20 overflow-y-auto px-6 pb-6 space-y-3 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700">
      <!-- Loading/Empty State -->
      <div v-if="!event_items.length" class="flex flex-col items-center justify-center py-12 text-center">
        <svg v-if="loading" class="w-8 h-8 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4" />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <p v-else class="text-gray-500 dark:text-gray-400 font-medium">
          {{ $t("No events on this day") }}
        </p>
      </div>

      <!-- Event Cards -->
      <div v-for="item in event_items" :key="item.id" class="group relative">
        <!-- Color Accent Bar -->
        <div
          class="absolute left-0 top-0 w-1 h-full rounded-l-lg transition-all group-hover:w-1.5"
          :style="{
            background: `linear-gradient(180deg, ${
              item.source == 'interactions'
                ? '#5CE0A9'
                : item.source == 'goals'
                  ? '#59BEE5'
                  : item.source == 'tasks'
                    ? '#B171F9'
                    : '#FFBE15'
            } 0%, ${
              item.source == 'interactions'
                ? '#2AC986'
                : item.source == 'goals'
                  ? '#118EBE'
                  : item.source == 'tasks'
                    ? '#841FF4'
                    : '#FFBE15'
            } 100%)`
          }" />

        <!-- Card Content -->
        <div
          class="pl-5 pr-4 py-4 rounded-lg bg-white dark:bg-dark-screen-background border border-gray-200 dark:border-gray-700 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-600 transition-all cursor-pointer"
          @click="handleDetailsClick(item)">
          <div class="flex items-start justify-between gap-4">
            <!-- Event Info -->
            <div class="flex-1 min-w-0">
              <!-- Title Row -->
              <div class="flex items-center gap-2 mb-2">
                <GoogleIcon v-if="item.origin == 'gmail' && item.type == 'meeting'" class="w-5 h-5 flex-shrink-0" />
                <GmailIcon v-if="item.origin == 'gmail' && item.type == 'email'" class="w-5 h-5 flex-shrink-0" />
                <OutlookIcon v-if="item.origin == 'outlook' && item.type == 'meeting'" class="w-5 h-5 flex-shrink-0" />

                <!-- Category Badge -->
                <span
                  :class="[
                    'px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide',
                    item.source == 'interactions'
                      ? 'bg-[#5CE0A9]/20 text-[#2AC986]'
                      : item.source == 'goals'
                        ? 'bg-[#59BEE5]/20 text-[#118EBE]'
                        : item.source == 'tasks'
                          ? 'bg-[#B171F9]/20 text-[#841FF4]'
                          : 'bg-[#FFBE15]/20 text-[#FFBE15]'
                  ]">
                  {{ item.source == "interactions" ? item.type : item.source.slice(0, item.source.length - 1) }}
                </span>
              </div>

              <!-- Event Title -->
              <h3 class="text-base font-semibold text-gray-900 dark:text-white truncate mb-1">
                {{ item.name || "(No title)" }}
                <span v-if="item.record" class="text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({{ Record.from(item?.record)?.name || "" }})
                </span>
              </h3>

              <!-- Date and Time -->
              <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>{{
                  $moment
                    .utc(item.source == "interactions" ? item.interaction_date : item.due_date)
                    .local()
                    .format("DD MMM YYYY")
                }}</span>
                <span v-if="item.meta_data?.when?.object == 'timespan'" class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {{ $moment.unix(item.meta_data.when.start_time).format("hh:mm A") }} -
                  {{ $moment.unix(item.meta_data.when.end_time).format("hh:mm A") }}
                </span>
              </div>
            </div>

            <!-- Join Meeting Button -->
            <a
              v-if="item.meta_data?.conferencing"
              target="_blank"
              :href="item.meta_data?.conferencing?.details?.url"
              class="flex-shrink-0 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-medium shadow-sm hover:shadow-md transition-all flex items-center gap-2"
              @click.stop>
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <div>
                <div>{{ $t("Join Meeting") }}</div>
                <div v-if="item.meta_data?.conferencing?.details?.pin" class="text-xs opacity-90">
                  {{ $t("PIN") }}: {{ item.meta_data.conferencing.details.pin }}
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- Add Meeting Button -->
      <button
        :disabled="editing_disabled"
        :class="[
          'w-full mt-4 px-6 py-3 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2',
          editing_disabled
            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
        ]"
        @click="editing_disabled ? null : handleCreateMeetingClick()">
        <Plus class="w-5 h-5" />
        {{ $t("Add Meeting") }}
      </button>
      <p v-if="editing_disabled" class="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
        {{ $t("You don't have access to this feature") }}
      </p>
    </div>

    <!-- Event Creation Modal -->
    <Modal
      v-model:is-open="create_new_event"
      max-width="max-w-5xl"
      :default-success-msg="$t('Meeting created successfully')"
      :use-toast="true">
      <template #form="{ close: closeModal, done: showIsDone }">
        <div class="space-y-6">
          <!-- Modal Header -->
          <div class="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-3">
              <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <svg class="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
                  {{ $t("Create New Meeting") }}
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {{ $t("Schedule a meeting with your team") }}
                </p>
              </div>
            </div>
          </div>

          <!-- Integration Selector Cards -->
          <div v-if="available_calendar_integrations.length > 0" class="space-y-3">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
              {{ $t("Select Calendar Integration") }}
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                v-for="integration in available_calendar_integrations"
                :key="integration.id"
                type="button"
                :class="[
                  'relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all transform hover:scale-[1.02]',
                  selected_integration?.id === integration.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-screen-background hover:border-blue-300 dark:hover:border-blue-600'
                ]"
                @click="selected_integration = integration">
                <!-- Icon -->
                <div
                  :class="[
                    'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all',
                    selected_integration?.id === integration.id ? 'bg-white dark:bg-gray-800 shadow-sm' : 'bg-gray-50 dark:bg-gray-800'
                  ]">
                  <GoogleIcon v-if="integration.reference === 'google-calendar'" class="w-7 h-7" />
                  <OutlookIcon v-if="integration.reference === 'outlook' || integration.name === 'outlook'" class="w-7 h-7" />
                </div>

                <!-- Info -->
                <div class="flex-1 text-left min-w-0">
                  <h4 class="font-semibold text-gray-900 dark:text-white text-sm truncate">
                    {{ integration.name || integration.reference }}
                  </h4>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {{
                      integration.reference === "google-calendar"
                        ? "Google Calendar"
                        : integration.reference === "outlook"
                          ? "Outlook Calendar"
                          : integration.reference
                    }}
                  </p>
                </div>

                <!-- Selected Badge -->
                <div v-if="selected_integration?.id === integration.id" class="flex-shrink-0">
                  <div class="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd" />
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <!-- Event Form -->
          <EventForm
            :view-option="viewOption"
            :selected-date="selected_date"
            :origin="selected_integration?.origin || origin"
            :integration-name="selected_integration?.name || selected_integration?.reference"
            @close-modal="closeModal"
            @done="showIsDone" />
        </div>
      </template>
    </Modal>
    <ActionDropDown
      v-if="selected_meeting?.type == 'email'"
      :icon="() => dots_icon()"
      :icon-button="true"
      :actions="getEmailActions(selected_meeting)" />
    <Reply class="w-0 h-0" />
    <Forward class="w-0 h-0" />
  </div>

  <div v-else class="relative p-4 mx-4 mt-10 mb-10 bg-white rounded-lg dark:bg-dark-box-background">
    <div class="flex items-center align-middle">
      <div class="flex items-center">
        <div>
          <ArrowLeft class="w-6 h-6 mr-2 cursor-pointer text-common-grey" @click="selected_meeting = null" />
        </div>
      </div>

      <EllipsisVertical class="absolute opacity-0 -z-10" />

      <div class="ml-10 text-lg font-bold dark:text-white">
        {{ selected_meeting.name }}
      </div>
      <div class="ml-auto mr-4 dark:text-white">
        {{ $moment(selected_meeting.interaction_date).format("DD MMM YYYY") }}
      </div>
    </div>

    <div v-if="selected_meeting.meta_data" class="mt-5 font-light text-common-grey">
      <span class="mr-3 font-bold">{{ $t("Time") }}:</span>
      <span class="ml-4">{{ $moment(selected_meeting.interaction_date).format("dddd MMM DD, YYYY") }}</span>
      -
      <span class="ml-2">{{
        selected_meeting.meta_data?.when?.object == "timespan"
          ? `${$moment.unix(selected_meeting.meta_data.when.start_time).format("hh:mm A")} - ${$moment
            .unix(selected_meeting.meta_data.when.end_time)
            .format("hh:mm A")}`
          : ""
      }}</span>
    </div>

    <div
      v-if="selected_meeting.meta_data.participants"
      :class="[` max-h-14 py-4 pt-2 mt-5  rounded-t-lg flex text-center  justify-center w-[340px]`]">
      <div class="text-common-grey font-bold mr-4 mt-0.5">
        {{ $t("Attending") }}:
      </div>
      <div
        :class="[
          `cursor-pointer w-1/3 border dark:border-dark-divider-border py-4 px-0 rounded-l-md   ${
            selected_meeting.meta_data.participants.filter((p) => p.email == $store.state.auth.user.email)[0]?.status == 'yes'
              ? ' text-white bg-light-brand-blue'
              : 'bg-white dark:bg-dark-screen-background dark:text-white'
          }`
        ]"
        @click="handleAttend('yes')">
        <span :class="[`relative -top-2.5`]">{{ $t("Yes") }} </span>
      </div>
      <div
        :class="[
          `cursor-pointer w-1/3 border dark:border-dark-divider-border py-4 px-0  ${
            selected_meeting.meta_data.participants.filter((p) => p.email == $store.state.auth.user.email)[0]?.status == 'no'
              ? ' text-white bg-blue'
              : 'bg-white dark:bg-dark-screen-background dark:text-white'
          }`
        ]"
        @click="handleAttend('no')">
        <span :class="[`relative -top-2.5`]">{{ $t("No") }} </span>
      </div>
      <div
        :class="[
          `cursor-pointer w-1/3 border dark:border-dark-divider-border py-4 px-0 rounded-r-md ${
            selected_meeting.meta_data.participants.filter((p) => p.email == $store.state.auth.user.email)[0]?.status == 'maybe'
              ? ' text-white bg-blue'
              : 'bg-white dark:bg-dark-screen-background dark:text-white'
          }`
        ]"
        @click="handleAttend('maybe')">
        <span :class="[`relative -top-2.5`]">{{ $t("Maybe") }} </span>
      </div>
    </div>

    <div v-if="selected_meeting.type == 'meeting'" :class="[` py-4 pt-2  rounded-t-lg   mt-3 flex`]">
      <div class="mr-4 font-bold text-common-grey">
        {{ $t("Participants") }}:
      </div>
      <div>
        <div v-for="participant in selected_meeting.meta_data?.participants" :key="participant.email" class="dark:text-white">
          {{ participant.email }} -
          <span
            :class="[
              `${
                participant.status == 'yes'
                  ? 'text-green-success'
                  : participant.status == 'no'
                    ? 'text-status-error-red'
                    : participant.status == 'maybe'
                      ? 'text-status-warning-yellow'
                      : 'text-common-grey'
              }`
            ]">
            {{
              participant.status == "yes"
                ? $t("Attending")
                : participant.status == "no"
                  ? $t("Not Attending")
                  : participant.status == "maybe"
                    ? $t("Maybe")
                    : $t("No Reply")
            }}
          </span>
        </div>
      </div>
    </div>

    <a
      v-if="selected_meeting.meta_data?.conferencing && selected_meeting.type == 'meeting'"
      target="_blank"
      :href="selected_meeting.meta_data?.conferencing?.details?.url"
      class="relative btn primary-btn p-3 mr-5 rounded-md cursor-pointer text-white w-[350px] mt-2 mb-4 text-center">
      <span :class="[`${selected_meeting.meta_data?.conferencing?.details?.pin ? 'bottom-1.5 relative' : ''}`]">{{
        $t("Join Meeting")
      }}</span>
      <div v-if="selected_meeting.meta_data?.conferencing?.details?.pin" class="absolute text-xs bottom-0.5">
        {{ $t("PIN") }}: {{ selected_meeting.meta_data.conferencing.details.pin }}
      </div>
    </a>
  </div>
</template>

<script>
import { Calendar } from "v-calendar"
import ActionDropDown from "@/components/shared/ActionDropDown.vue"
import EventForm from "./EventFrom.vue"
import Modal from "@/components/shared/Modal.vue"
import GoogleIcon from "@/components/shared/icons/GoogleIcon.vue"
import GmailIcon from "@/components/shared/icons/GmailIcon.vue"
import OutlookIcon from "@/components/shared/icons/OutlookIcon.vue"
// import MicrosoftIcon from "@/components/shared/icons/MicrosoftIcon.vue"
import { Reply, Forward, Plus, EllipsisVertical, ArrowLeft, ArrowRight, Calendar1, CalendarDays } from "lucide-vue-next"
// import Avatar from "@/components/shared/Avatar.vue"
// import TextEditor from "@/components/shared/TextEditor.vue"
import TextInput from "@/components/shared/TextInput.vue"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import dataService from "@/services/data.service"
import { Record } from "@/models/record"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
// import dummy from "./dummy"

export default {
  components: {
    Plus,
    ArrowRight,
    Calendar,
    EventForm,
    Modal,
    GoogleIcon,
    GmailIcon,
    OutlookIcon,
    // MicrosoftIcon,
    Reply,
    Forward,
    ArrowLeft,
    EllipsisVertical,
    // Avatar,
    ActionDropDown,
    Calendar1,
    CalendarDays,
    TextInput,
    MultiSelectInput
  },
  inject: ["canUserEditLayout"],
  props: {
    initialFilter: {
      type: Object,
      default: () => ({})
    },
    label: {
      type: String
    },
    view_by: {
      type: Array,
      default: null
    },
    viewOption: {
      type: String,
      default: "month_view"
    }
  },
  emits: [],

  data() {
    return {
      Record,
      dots_icon: EllipsisVertical,
      create_new_event: false,
      is_calendar_connected: false,
      primary_calendar_integration: null,
      is_email_connected: false,
      origin: null,
      columns: 1,
      masks: {
        weekdays: "WWW"
      },
      event_items: [],
      selected_date: new Date(),
      attributes: [],

      selected_meeting: null,
      show_plans: true,
      show_goals: true,
      show_tasks: true,
      show_interactions: true,
      date_selected_attribute: [],
      search_term: "",
      team_members_selected: [],
      plotting_loading: false,
      loading: false,
      calendar_range_shown: null,
      available_calendar_integrations: [],
      selected_integration: null
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    }
  },
  watch: {
    initialFilter: {
      deep: true,
      handler() {
        this.plotActions(this.getDateRange(this.selected_date, this.columns))
        this.getActionsForDate()
      }
    },
    search_term() {
      this.getActionsForDate()
    },

    team_members_selected: {
      deep: true,
      handler() {
        this.getActionsForDate()
      }
    },

    show_plans() {
      this.plotActions(this.getDateRange(this.selected_date, this.columns))
      this.getActionsForDate()
    },
    show_goals() {
      this.plotActions(this.getDateRange(this.selected_date, this.columns))
      this.getActionsForDate()
    },
    show_tasks() {
      this.plotActions(this.getDateRange(this.selected_date, this.columns))
      this.getActionsForDate()
    },
    show_interactions() {
      this.plotActions(this.getDateRange(this.selected_date, this.columns))
      this.getActionsForDate()
    }
  },

  activated() {
    this.register_listeners()
  },

  deactivated() {
    this.unregister_listeners()
  },
  mounted() {
    this.handleDayClick()
  },
  unmounted() {
    this.unregister_listeners()
  },

  created() {
    this.checkIsCalendarConnected()
    this.checkIsEmailConnected()
    // this.plotActions()
    this.register_listeners()

    const today = new Date()
    this.handleUpdateCalendar({ month: today.getMonth() + 1, year: today.getFullYear() })

    this.date_selected_attribute = []

    this.date_selected_attribute.push({
      highlight: {
        style: {
          backgroundColor: this.$store.state.ui.darkMode ? "#a9abaa" : "#e3e8e5",
          width: "26px",
          height: "26px"
        }
      },
      dates: [new Date()]
    })

    if (this.view_by) {
      this.show_plans = this.view_by.includes("plans")
      this.show_goals = this.view_by.includes("goals")
      this.show_tasks = this.view_by.includes("tasks")
      this.show_interactions = this.view_by.includes("interactions")
    }
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.DARK_MODE_CHANGED, () => {
        this.date_selected_attribute = []
        const date_calc = this.selected_date

        if (
          date_calc.getDate() !== new Date().getDate() ||
          date_calc.getMonth() !== new Date().getMonth() ||
          date_calc.getFullYear() !== new Date().getFullYear()
        ) {
          this.date_selected_attribute.push({
            highlight: {
              style: {
                backgroundColor: this.$store.state.ui.darkMode ? "#43444f" : "#f5f5f5",
                width: "26px",
                height: "26px"
              }
            },
            dates: [new Date()]
          })
        }
        this.date_selected_attribute.push({
          highlight: {
            style: {
              backgroundColor: this.$store.state.ui.darkMode ? "#a9abaa" : "#e3e8e5",
              width: "26px",
              height: "26px"
            }
          },
          dates: date_calc
        })
      })
      this.$emitter.on(ALL_EMITTERS.INTERACTION_UPDATED_IN_CALENDAR, (interaction) => {
        setTimeout(() => {
          const calendar = this.$refs.calendar
          let meeting_date = this.$moment(interaction.interaction_date).toISOString()
          calendar.move({
            month: meeting_date.getMonth() + 1,
            year: meeting_date.getFullYear()
          })
          this.handleUpdateCalendar({
            month: meeting_date.getMonth() + 1,
            year: meeting_date.getFullYear()
          })
          this.handleDayClick({ range: { start: new Date(meeting_date) } })
          this.create_new_event = false
        }, 500)
      })
      this.$emitter.on(ALL_EMITTERS.NEW_MEETING_CREATED, (meeting_date) => {
        setTimeout(() => {
          const calendar = this.$refs.calendar
          calendar.move({
            month: meeting_date.getMonth() + 1,
            year: meeting_date.getFullYear()
          })
          this.handleUpdateCalendar({
            month: meeting_date.getMonth() + 1,
            year: meeting_date.getFullYear()
          })
          this.handleDayClick({ range: { start: new Date(meeting_date) } })
          this.create_new_event = false
        }, 500)
      })
      this.$emitter.on(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED, (meeting_date) => {
        if (meeting_date) {
          setTimeout(() => {
            if (this.columns == 1) {
              if (
                meeting_date.getMonth() + 1 == this.calendar_range_shown.month &&
                meeting_date.getFullYear() == this.calendar_range_shown.year
              ) {
                this.handleUpdateCalendar({
                  month: meeting_date.getMonth() + 1,
                  year: meeting_date.getFullYear()
                })
                if (this.selected_date.getDate() == meeting_date.getDate()) {
                  this.handleDayClick({ range: { start: new Date(meeting_date) } })
                }
              }
            } else if (this.columns == 2) {
              const month_1_compare = this.calendar_range_shown.month
              const month_2_compare = this.calendar_range_shown.month == 1 ? 12 : this.calendar_range_shown.month - 1
              const year_1_compare = this.calendar_range_shown.year
              const year_2_compare = this.calendar_range_shown.month == 1 ? year_1_compare - 1 : year_1_compare

              if (meeting_date.getMonth() + 1 == month_1_compare && meeting_date.getFullYear() == year_1_compare) {
                this.handleUpdateCalendar({
                  month: meeting_date.getMonth() + 1,
                  year: meeting_date.getFullYear()
                })
                if (this.selected_date.getDate() == meeting_date.getDate()) {
                  this.handleDayClick({ range: { start: new Date(meeting_date) } })
                }
              }
              if (meeting_date.getMonth() + 1 == month_2_compare && meeting_date.getFullYear() == year_2_compare) {
                this.handleUpdateCalendar({
                  month: meeting_date.getMonth() + 1,
                  year: meeting_date.getFullYear()
                })
                if (this.selected_date.getDate() == meeting_date.getDate()) {
                  this.handleDayClick({ range: { start: new Date(meeting_date) } })
                }
              }
            }
          }, 500)
        }
      })
      this.$emitter.on(ALL_EMITTERS.ACTION_DELETED, (meeting_date) => {
        if (meeting_date) {
          const meeting_date_date = new Date(meeting_date)
          if (this.columns == 1) {
            if (
              meeting_date_date.getMonth() + 1 == this.calendar_range_shown.month &&
              meeting_date_date.getFullYear() == this.calendar_range_shown.year
            ) {
              this.handleUpdateCalendar({
                month: meeting_date_date.getMonth() + 1,
                year: meeting_date_date.getFullYear()
              })
              if (this.selected_date.getDate() == meeting_date_date.getDate()) {
                this.handleDayClick({ range: { start: new Date(meeting_date_date) } })
              }
            }
          } else if (this.columns == 2) {
            const month_1_compare = this.calendar_range_shown.month
            const month_2_compare = this.calendar_range_shown.month == 1 ? 12 : this.calendar_range_shown.month - 1
            const year_1_compare = this.calendar_range_shown.year
            const year_2_compare = this.calendar_range_shown.month == 1 ? year_1_compare - 1 : year_1_compare

            if (meeting_date_date.getMonth() + 1 == month_1_compare && meeting_date_date.getFullYear() == year_1_compare) {
              this.handleUpdateCalendar({
                month: meeting_date_date.getMonth() + 1,
                year: meeting_date_date.getFullYear()
              })
              if (this.selected_date.getDate() == meeting_date_date.getDate()) {
                this.handleDayClick({ range: { start: new Date(meeting_date) } })
              }
            }
            if (meeting_date_date.getMonth() + 1 == month_2_compare && meeting_date_date.getFullYear() == year_2_compare) {
              this.handleUpdateCalendar({
                month: meeting_date_date.getMonth() + 1,
                year: meeting_date_date.getFullYear()
              })
              if (this.selected_date.getDate() == meeting_date_date.getDate()) {
                this.handleDayClick({ range: { start: new Date(meeting_date) } })
              }
            }
          }
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.DARK_MODE_CHANGED)
      this.$emitter.off(ALL_EMITTERS.INTERACTION_UPDATED_IN_CALENDAR)
      this.$emitter.off(ALL_EMITTERS.NEW_MEETING_CREATED)
      this.$emitter.off(ALL_EMITTERS.NEW_ACTINON_CREATED_OR_UPDATED)
      this.$emitter.off(ALL_EMITTERS.ACTION_DELETED)
    },
    plotActions(date_range) {
      this.attributes = []
      let data_params = {
        from_date: this.$moment(date_range.from_date).startOf("day").toISOString(),
        to_date: this.$moment(date_range.to_date).endOf("day").toISOString()
      }
      if (!this.$utils.isEmpty(this.initialFilter)) {
        data_params["initial_filters"] = this.initialFilter
        if (data_params["initial_filters"].companies?.length) {
          data_params["initial_filters"].companies = this.$utils.clone(
            data_params["initial_filters"].companies.map((c) => (c == "this_record" ? this.$route.params.cid : c))
          )
        }
      }
      // let params = { form_date: this.$moment.utc(date_range.from_date).format("L"), to_date: this.$moment.utc(date_range.to_date).format("L") }

      if (!this.plotting_loading) {
        this.plotting_loading = true
        dataService.getCalendarRanges(data_params).then((res) => {
          const plans_array_dates = []
          const goals_array_dates = []
          const tasks_array_dates = []
          const interactions_array_dates = []

          for (const date_key in res.data) {
            if (res.data[date_key].plans_count) {
              {
                plans_array_dates.push(this.$moment(date_key).local().toDate())
              }
            }

            if (res.data[date_key].goals_count) {
              {
                goals_array_dates.push(this.$moment(date_key).local().toDate())
              }
            }

            if (res.data[date_key].tasks_count) {
              tasks_array_dates.push(this.$moment(date_key).local().toDate())
            }

            if (res.data[date_key].interaction_count) {
              interactions_array_dates.push(this.$moment(date_key).local().toDate())
            }
          }

          if (plans_array_dates.length && this.show_plans) {
            const plans_att_obj = {
              dot: {
                style: {
                  backgroundColor: "#FFBE15",
                  width: "6px",
                  height: "6px"
                }
              },
              dates: plans_array_dates
            }
            this.attributes.push(plans_att_obj)
          }

          if (goals_array_dates.length && this.show_goals) {
            const goals_att_obj = {
              dot: {
                style: {
                  backgroundColor: "#59BEE5",
                  width: "6px",
                  height: "6px"
                }
              },
              dates: goals_array_dates
            }
            this.attributes.push(goals_att_obj)
          }

          if (tasks_array_dates.length && this.show_tasks) {
            const tasks_att_obj = {
              dot: {
                style: {
                  backgroundColor: "#B171F9",
                  width: "6px",
                  height: "6px"
                }
              },
              dates: tasks_array_dates
            }
            this.attributes.push(tasks_att_obj)
          }

          if (interactions_array_dates.length && this.show_interactions) {
            const interactions_att_obj = {
              dot: {
                style: {
                  backgroundColor: "#5CE0A9",
                  width: "6px",
                  height: "6px"
                }
              },
              dates: interactions_array_dates
            }
            this.attributes.push(interactions_att_obj)
          }
          this.plotting_loading = false
        })
      }
    },
    checkIsCalendarConnected() {
      // Get all active calendar integrations
      this.available_calendar_integrations = this.$store.state.integration.integrations.filter(
        (i) =>
          ((i.reference == "outlook" && i.scope?.includes("Calendars")) || (i.reference == "google-calendar" && i.status == "on")) &&
          i.status == "on"
      )

      this.is_calendar_connected = this.available_calendar_integrations.length > 0

      // Set primary integration (first available)
      this.primary_calendar_integration = this.available_calendar_integrations[0] || null

      // Set selected integration to the first available
      this.selected_integration = this.available_calendar_integrations[0] || null
    },
    checkIsEmailConnected() {
      this.is_email_connected =
        this.$store.state.integration.integrations.filter(
          (i) =>
            (i.reference == "gmail" || i.reference == "outlook") &&
            i.status == "on" &&
            i?.service_provider == this.primary_calendar_integration?.service_provider
        ).length > 0
    },
    handleCreateMeetingClick() {
      if (this.is_calendar_connected) {
        if (!this.is_email_connected) {
          this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
            message: "In order to send a meeting invite please link your account to Google Calendar or outlook Calendar."
          })
        } else {
          // Use the selected integration, or default to the first available
          const integration_to_use = this.selected_integration || this.available_calendar_integrations[0]
          this.origin = integration_to_use?.origin || this.primary_calendar_integration?.origin
          this.create_new_event = true
        }
      } else {
        this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
          message: "Calendar integration is not active. Activate integration to create a meeting"
        })
      }
    },
    handleSearch(e) {
      this.search_term = e
    },
    handleAttend(status) {
      let index = this.selected_meeting.meta_data.participants.findIndex((p) => p.email == this.$store.state.auth.user.email)
      const found_user = this.selected_meeting.meta_data.participants[index]
      this.selected_meeting.meta_data.participants.splice(index, 1, {
        ...found_user,
        status: status
      })

      //Perform BE request
    },

    getEmailActions(email) {
      return [
        {
          name: "Reply",
          action: () => {
            const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
            if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
              if (i_service.status == "on") {
                this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
                  records: [],
                  record: { ...email.meta_data.from[0], type: "team_members" },
                  content: email.description,
                  ref_record: email,
                  reply: true
                })
              } else {
                this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                  message: "Email integration is not active, Activate integration to send an email"
                })
              }
            } else {
              this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                message: "In order to send an email please link your account to your Email."
              })
            }
          },
          icon: Reply
        },
        {
          name: "Reply to All",
          action: () => {
            const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
            if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
              if (i_service.status == "on") {
                const all_emails = email.meta_data.to.concat(email.meta_data?.cc)
                const filtered_emails = all_emails.filter((email) => email.email !== this.$store.state.auth.user.email)
                const mapped_emails = filtered_emails.map((email) => {
                  return { ...email, type: "team_members" }
                })
                this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
                  cc_records: mapped_emails,
                  record: { ...email.meta_data.from[0], type: "team_members" },
                  content: email.description,
                  ref_record: email,
                  reply: true
                })
              } else {
                this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                  message: "Email integration is not active, Activate integration to send an email"
                })
              }
            } else {
              this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                message: "In order to send an email please link your account to your Email."
              })
            }
          },
          icon: Reply
        },
        {
          name: "Forward",
          action: () => {
            const i_service = this.$store.state.integration.integrations.find((i) => i.name == "gmail")
            if (i_service && i_service.scope.includes("https://www.googleapis.com/auth/gmail.modify")) {
              if (i_service.status == "on") {
                this.$emitter.emit(ALL_EMITTERS.SEND_EMAIL, {
                  records: [],
                  content: email.description,
                  ref_record: email
                })
              } else {
                this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                  message: "Email integration is not active, Activate integration to send an email"
                })
              }
            } else {
              this.$emitter.emit(ALL_EMITTERS.INTEGRATION_MISSING_MODAL, {
                message: "In order to send an email please link your account to your Email."
              })
            }
          },
          icon: Forward
        }
      ]
    },

    handleDetailsClick(item) {
      if (item.source == "interactions") {
        if (item.origin == "gmail" || item.origin == "outlook") {
          if (item.meta_data?.conferencing) {
            this.selected_meeting = item
          }
        } else {
          this.$emitter.emit(ALL_EMITTERS.UPDATE_INTERACTION, {
            interaction: this.$utils.clone(item)
          })
        }
      } else if (item.source == "tasks") {
        this.$emitter.emit(ALL_EMITTERS.UPDATE_TASK, { task: this.$utils.clone(item) })
      } else if (item.source == "goals") {
        this.$emitter.emit(ALL_EMITTERS.UPDATE_GOAL, { goal: this.$utils.clone(item) })
      } else if (item.source == "plans") {
        this.$emitter.emit(ALL_EMITTERS.UPDATE_PLAN, { plan: this.$utils.clone(item) })
      }
    },
    getDateRange(current_date, range = 1) {
      return {
        from_date: this.$moment(current_date).startOf("month").startOf("day").toDate(),
        to_date: this.$moment(current_date).startOf("month").add(range, "months").endOf("month").endOf("day").toDate()
      }
    },
    handleUpdateCalendar(range) {
      let range_calc = null
      if (range[0]) {
        range_calc = range[0]
      } else {
        range_calc = range
      }
      if (range_calc) {
        this.calendar_range_shown = range_calc
        let selected_date = null
        if (this.columns == 1) {
          selected_date = this.$moment()
            .set("date", 1)
            .set("month", range_calc.month - 1)
            .set("year", range_calc.year)
            .toDate()
        } else {
          selected_date = this.$moment()
            .set("date", 1)
            .set("month", range_calc.month == 12 ? 1 : range_calc.month - 1)
            .set("year", range_calc.year)
            .toDate()
        }
        if (!this.plotting_loading) {
          this.plotActions(this.getDateRange(selected_date, this.columns))
        }
      }
    },

    getActionsForDate() {
      if (!this.loading) {
        this.event_items = []
        this.loading = true
        const params_calc = {
          page_size: 25,
          expand: "record,contacts"
        }

        params_calc["due_date_start"] = this.$moment(this.selected_date).startOf("day").toISOString()
        params_calc["due_date_end"] = this.$moment(this.selected_date).endOf("day").toISOString()

        if (this.search_term) {
          params_calc["name"] = this.search_term
        }

        if (this.team_members_selected.length) {
          params_calc["assigned_to"] = this.team_members_selected.join(",")
        }

        if (!this.$utils.isEmpty(this.initialFilter)) {
          params_calc["records"] = this.initialFilter?.companies?.join(",").replaceAll("this_record", this.$route.params.cid) || []

          params_calc["companies"] = this.initialFilter?.companies?.join(",").replaceAll("this_record", this.$route.params.cid) || []
          params_calc["category"] = this.initialFilter?.categories?.join(",") || []
          params_calc["assigned_to"] = this.initialFilter?.collaborators?.join(",") || []
          params_calc["users"] = this.initialFilter?.collaborators?.join(",") || []
          params_calc["contacts"] = this.initialFilter?.contacts?.join(",") || []
          params_calc["status"] = this.initialFilter?.status?.join(",") || []

          if (this.initialFilter?.types) {
            params_calc["type"] = this.$store.state.data.interaction_types
              .filter((t) => this.initialFilter.types.includes(t.value))
              .map((t) => t.value)
              .filter((t) => t !== "email")
              .join(",")
          }
        } else {
          params_calc["type"] = this.$store.state.data.interaction_types
            .map((t) => t.value)
            .filter((t) => t !== "email")
            .join(",")
        }

        if (this.team_members_selected.length) {
          params_calc["users"] = this.team_members_selected.join(",")
        }

        if (this.show_interactions) {
          params_calc["interaction_date_start"] = this.$moment(this.selected_date).startOf("day").toISOString()
          params_calc["interaction_date_end"] = this.$moment(this.selected_date).endOf("day").toISOString()
        }

        dataService.getCalendarActivities(params_calc).then((res) => {
          if (this.show_goals) {
            res.data.goals.results.forEach((item) => {
              this.event_items.push({ ...item, source: "goals" })
            })
          }

          if (this.show_tasks) {
            res.data.tasks.results.forEach((item) => {
              this.event_items.push({ ...item, source: "tasks" })
            })
          }

          if (this.show_plans) {
            res.data.plans.results.forEach((item) => {
              this.event_items.push({ ...item, source: "plans" })
            })
          }

          if (this.show_interactions) {
            res.data.interactions.results.forEach((item) => {
              this.event_items.push({ ...item, source: "interactions" })
            })
          }

          this.loading = false
        })
      }
    },

    handleDayClick(date) {
      this.date_selected_attribute = []
      let date_calc = this.selected_date ? this.selected_date : new Date()
      if (date) {
        if (date.range?.start) {
          date_calc = date.range?.start
        } else if (date.date) {
          date_calc = date.date
        } else {
          date_calc = new Date()
        }
      }
      // date?this.$moment().set('date',date.day).set('month',date.month).set('year',date.year).toDate():this.selected_date?this.selected_date:new Date()

      if (
        date_calc.getDate() !== new Date().getDate() ||
        date_calc.getMonth() !== new Date().getMonth() ||
        date_calc.getFullYear() !== new Date().getFullYear()
      ) {
        this.date_selected_attribute.push({
          highlight: {
            style: {
              backgroundColor: this.$store.state.ui.darkMode ? "#43444f" : "#f5f5f5",
              width: "26px",
              height: "26px"
            }
          },
          dates: [new Date()]
        })
      }
      this.date_selected_attribute.push({
        highlight: {
          style: {
            backgroundColor: this.$store.state.ui.darkMode ? "#a9abaa" : "#e3e8e5",
            width: "26px",
            height: "26px"
          }
        },
        dates: date_calc
      })

      this.selected_date = date_calc
      this.getActionsForDate()

      // this.filterEvents()
    }
  }
}
</script>

<style scoped>
.custom-calendar {
  width: 100%;
  border-width: 0;
  border-radius: 0;
  padding: 1.5rem;
}

.custom-calendar-dark {
  width: 100%;
  border-width: 0;
  border-radius: 0;
  padding: 1.5rem;
}

/* Custom scrollbar styles */
.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  border-radius: 3px;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
}

.dark .scrollbar-thumb-gray-700::-webkit-scrollbar-thumb {
  background-color: #374151;
}

.scrollbar-thumb-gray-300::-webkit-scrollbar-thumb:hover {
  background-color: #9ca3af;
}

.dark .scrollbar-thumb-gray-700::-webkit-scrollbar-thumb:hover {
  background-color: #4b5563;
}
</style>
